import type { OperatorRunStatus } from '~/hooks/queries/use-operator-runs';

/**
 * FRONT-2341 — the one place that decides what a desktop task's Stop
 * control looks like, shared by the chat transcript's `DesktopTaskCard`
 * (`desktop-task-card-model.ts`'s `stopControl`, which wraps this) and the
 * full `OperatorPanel`. Same "derive the display from one pure function so
 * two surfaces can't disagree" split as `operator-run-status.ts`.
 *
 * A stop is not instant: `stopRequestedAt` (the backend's
 * `OperatorRunListWire` field) is set the moment a stop is asked for, but
 * the run keeps reading `status: "running"` until the loop notices at its
 * next checkpoint (or the stop-watch force-finishes it). Before FRONT-2341
 * the button had no way to show that in-between state — it just snapped
 * back to a plain enabled "Stop" the instant the mutation settled, which is
 * why people pressed it repeatedly.
 *
 * Four states, in order:
 * 1. Not running at all → no control (`'hidden'`).
 * 2. Running, no stop requested, no request in flight → plain `'idle'`
 *    "Stop", enabled.
 * 3. A stop request is in flight, OR one was requested less than
 *    {@link STOP_SLOW_THRESHOLD_MS} ago → `'stopping'`, "Stopping…",
 *    disabled. `pending` is checked FIRST (before the elapsed time), so a
 *    second click sent from the `'slow'` state below shows this
 *    transitional state too, even though the original request is already
 *    past the threshold — the backend's stop is idempotent
 *    (`coalesce(stop_requested_at, now())`), so the second click never
 *    resets the clock; it just gives the person visible feedback that their
 *    click landed.
 * 4. A stop was requested at least {@link STOP_SLOW_THRESHOLD_MS} ago, the
 *    run is still `running`, and nothing is currently in flight →
 *    `'slow'`, "Stop again" (a second press sends the same idempotent
 *    stop), enabled, with a visible note — never a tooltip, this has to
 *    read on a touch screen too.
 *
 * `disabled` is derived FROM the phase (FRONT-1670's "a disabled control
 * and its explanation are one decision" rule) — the only disabled phase is
 * `'stopping'`, whose own label ("Stopping…") IS the explanation, so there
 * is no separate reason string to carry.
 */
export const STOP_SLOW_THRESHOLD_MS = 30_000;

export type StopPhase = 'hidden' | 'idle' | 'stopping' | 'slow';

export interface StopState {
  phase: StopPhase;
  show: boolean;
  label: string;
  disabled: boolean;
  /** Visible text explaining a slow stop, or `null`. Render this as a plain
   *  text node next to/below the button — never as a `tooltip` prop, which
   *  a touch screen never sees. */
  note: string | null;
}

export interface StopStateInput {
  status: OperatorRunStatus;
  /** The run's own `stopRequestedAt` (ISO timestamp), or `null` if no stop
   *  has been requested for it. */
  stopRequestedAt: string | null;
  /** Whether THIS tab has a stop mutation for this run currently in
   *  flight. */
  pending: boolean;
  /** `Date.now()` at render time — threaded in explicitly so this stays a
   *  pure function the sweep test can drive across the threshold without a
   *  fake clock. */
  now: number;
  /** When THIS browser first saw `stopRequestedAt` set for this run, on the
   *  browser's own clock ({@link stopObservedAt}), or `null`. Whenever this
   *  is set, the elapsed time is measured entirely on the browser's OWN
   *  clock (`now` minus this) — never mixed with the server's
   *  `stopRequestedAt` timestamp, so a laptop clock running either ahead of
   *  or behind the server's can't distort the 30s window (a clock running
   *  BEHIND the server's, mixed with the server's own timestamp, would read
   *  as a negative/tiny elapsed time and leave "Stopping…" disabled for as
   *  long as the skew). `stopRequestedAt` is used as the clock start only
   *  when there is no first sighting yet. */
  observedAt: number | null;
}

/** The moment the slow-stop clock starts: this browser's own first sighting
 *  of the stop when one is recorded (so both ends of the elapsed-time
 *  subtraction are on the SAME clock, immune to server/browser skew in
 *  either direction) — falling back to the server's `stopRequestedAt` only
 *  when no first sighting exists yet. */
function stopClockStart(stopRequestedAt: string | null, observedAt: number | null): number | null {
  if (stopRequestedAt === null) return null;
  if (observedAt !== null) return observedAt;
  const requestedAtMs = Date.parse(stopRequestedAt);
  return Number.isNaN(requestedAtMs) ? null : requestedAtMs;
}

const HIDDEN: StopState = { phase: 'hidden', show: false, label: 'Stop', disabled: false, note: null };

export function deriveStopState(input: StopStateInput): StopState {
  if (input.status !== 'running') return HIDDEN;

  const clockStart = stopClockStart(input.stopRequestedAt, input.observedAt);
  const elapsedMs = clockStart === null ? null : input.now - clockStart;
  const stopIsWithinThreshold = elapsedMs !== null && elapsedMs < STOP_SLOW_THRESHOLD_MS;

  // Order matters — see point 3 in the docblock above: `pending` wins over
  // an already-slow elapsed time.
  if (input.pending || stopIsWithinThreshold) {
    return { phase: 'stopping', show: true, label: 'Stopping…', disabled: true, note: null };
  }
  if (elapsedMs !== null && elapsedMs >= STOP_SLOW_THRESHOLD_MS) {
    return {
      phase: 'slow',
      show: true,
      label: 'Stop again',
      disabled: false,
      note: 'Still stopping — this is taking longer than usual.',
    };
  }
  return { phase: 'idle', show: true, label: 'Stop', disabled: false, note: null };
}

/**
 * How long until a pending stop would cross {@link STOP_SLOW_THRESHOLD_MS},
 * measured from `now` on the same clock start {@link deriveStopState} uses
 * (`observedAt` when there is one, else `stopRequestedAt` — see
 * {@link stopClockStart}) — `null` when there is nothing to wait for (no
 * stop requested, an unparseable timestamp, or the
 * threshold has already passed). Used by `use-stop-boundary-timer.ts` to
 * schedule its one-shot re-render: TanStack's structural sharing means an
 * unchanged poll response never re-renders the caller on its own, so a
 * mounted card/panel would otherwise sit on "Stopping…" past the threshold
 * until something else happened to re-render it. Pulled out as a pure
 * function so the arithmetic is unit-testable even though the effect
 * wiring around it isn't — this repo's frontend unit harness never runs
 * `useEffect` (no jsdom), so the hook's actual scheduling is proven by the
 * `@verify` browser specs instead (E2E-CHAT-STOP-01 and
 * E2E-MACHINE-DESKTOP-08 cross the threshold on a fake page clock with the
 * card / panel open).
 */
export function msUntilStopBoundary(
  stopRequestedAt: string | null,
  observedAt: number | null,
  now: number,
): number | null {
  const clockStart = stopClockStart(stopRequestedAt, observedAt);
  if (clockStart === null) return null;
  const remaining = clockStart + STOP_SLOW_THRESHOLD_MS - now;
  return remaining > 0 ? remaining : null;
}

/**
 * This browser's first sighting of a stop, per run id, on its own clock.
 * Deliberately module state, not React state: the chat card and the panel
 * are separate components (and a card can remount on scroll), and all of
 * them must start the slow-stop clock from the SAME first sighting. Entries
 * are tiny (one number per run ever stopped in this tab) and die with it.
 */
const firstObservedStop = new Map<string, number>();

/**
 * Returns when this browser first saw `stopRequestedAt` set for `runId`,
 * recording `now` as that moment on the first call that sees it. `null`
 * (and nothing recorded) while no stop has been requested. Idempotent after
 * the first sighting, so calling it during render is safe.
 */
export function stopObservedAt(runId: string, stopRequestedAt: string | null, now: number): number | null {
  if (stopRequestedAt === null) return null;
  const seen = firstObservedStop.get(runId);
  if (seen !== undefined) return seen;
  firstObservedStop.set(runId, now);
  return now;
}

/** Test-only: forget every recorded sighting. */
export function resetObservedStopsForTest(): void {
  firstObservedStop.clear();
}
